import L from 'leaflet'

// ── Types ──────────────────────────────────────────────────────────────

export interface BrokerPin {
  id: string
  name: string
  company: string | null
  territory: string
  tier: string
  sentiment: string | null
  activeDeals: number
}

export interface InquiryPin {
  id: string
  city: string
  country: string
  type: string
  stage: string
}

export interface ShowPin {
  id: string
  title: string
  city: string
  country: string
  date: string
  venue: string
  stage: string
  tourName: string | null
  legName: string | null
}

export interface CrewPin {
  id: string
  name: string
  baseCity: string
  baseCountry: string
  lat: number | null
  lng: number | null
  primaryRole: string
}

export interface VenuePin {
  id: string
  name: string
  city: string
  country: string
  lat: number | null
  lng: number | null
  tier: string
  capacityMax: number | null
}

export interface FanDemandPin {
  cityKey: string
  city: string
  country: string | null
  lat: number
  lng: number
  requestCount: number
  sampleComments: Array<{ text: string; commenterName: string | null }>
}

export interface MapData {
  brokers?: BrokerPin[]
  inquiries?: InquiryPin[]
  shows?: ShowPin[]
  crew?: CrewPin[]
  venues?: VenuePin[]
  fan_demand?: FanDemandPin[]
}

export interface TourOption {
  id: string
  name: string
}

export interface LegOption {
  id: string
  name: string
}

export type LayerName = 'brokers' | 'inquiries' | 'shows' | 'crew' | 'venues' | 'fan_demand'

// ── Constants ──────────────────────────────────────────────────────────

export const LAYER_COLOURS: Record<LayerName, string> = {
  brokers: '#3b82f6',
  inquiries: '#f97316',
  shows: '#ef4444',
  crew: '#22c55e',
  venues: '#a855f7',
  fan_demand: '#0ea5e9',
}

export const LAYER_LABELS: Record<LayerName, string> = {
  brokers: 'Brokers',
  inquiries: 'Inquiries',
  shows: 'Shows',
  crew: 'Crew',
  venues: 'Venues',
  fan_demand: 'Fan concert requests',
}

export const ALL_LAYERS: LayerName[] = [
  'brokers',
  'inquiries',
  'shows',
  'crew',
  'venues',
  'fan_demand',
]

export function parseLayersParam(raw: string | null): Set<LayerName> | null {
  if (!raw) return null
  const names = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is LayerName => (ALL_LAYERS as readonly string[]).includes(s))
  return names.length > 0 ? new Set(names) : null
}

/** Default Leaflet view — full world at low zoom. */
export const WORLD_MAP_CENTER: [number, number] = [20, 0]
export const WORLD_MAP_ZOOM = 2

/** Zoom the map to show fan-demand pins when present. */
export function fitMapToFanDemandPins(
  map: L.Map,
  pins: FanDemandPin[],
): void {
  if (pins.length === 0) return
  if (pins.length === 1) {
    map.setView([pins[0]!.lat, pins[0]!.lng], 6)
    return
  }
  const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng] as [number, number]))
  map.fitBounds(bounds, { padding: [48, 48], maxZoom: 6 })
}

export const RADIUS_OPTIONS = [
  { label: '10 mi', miles: 10 },
  { label: '25 mi', miles: 25 },
  { label: '50 mi', miles: 50 },
  { label: '100 mi', miles: 100 },
  { label: '200 mi', miles: 200 },
] as const

export const MILES_TO_KM = 1.60934

/** Approximate coords for territory centres (brokers without lat/lng) */
export const TERRITORY_COORDS: Record<string, [number, number]> = {
  north_america: [39.8, -98.5],
  europe_uk: [51.5, -0.1],
  middle_east: [25.3, 55.3],
  apac: [35.7, 139.7],
  latin_america: [-23.5, -46.6],
  africa: [-1.3, 36.8],
  corporate_private: [40.7, -74.0],
}

/** Approximate coords for major cities (fallback when no lat/lng in DB) */
export const CITY_COORDS: Record<string, [number, number]> = {
  london: [51.51, -0.13],
  'new york': [40.71, -74.01],
  'los angeles': [34.05, -118.24],
  chicago: [41.88, -87.63],
  manchester: [53.48, -2.24],
  birmingham: [52.49, -1.89],
  edinburgh: [55.95, -3.19],
  glasgow: [55.86, -4.25],
  paris: [48.86, 2.35],
  berlin: [52.52, 13.41],
  tokyo: [35.68, 139.69],
  sydney: [-33.87, 151.21],
  dubai: [25.20, 55.27],
  'las vegas': [36.17, -115.14],
  miami: [25.76, -80.19],
  toronto: [43.65, -79.38],
  atlanta: [33.75, -84.39],
  nashville: [36.16, -86.78],
  'san francisco': [37.77, -122.42],
  dallas: [32.78, -96.80],
  houston: [29.76, -95.37],
  denver: [39.74, -104.99],
  seattle: [47.61, -122.33],
  boston: [42.36, -71.06],
  philadelphia: [39.95, -75.17],
  amsterdam: [52.37, 4.90],
  dublin: [53.35, -6.26],
  madrid: [40.42, -3.70],
  rome: [41.90, 12.50],
  milan: [45.46, 9.19],
  melbourne: [-37.81, 144.96],
  auckland: [-36.85, 174.76],
  singapore: [1.35, 103.82],
  'hong kong': [22.32, 114.17],
  mumbai: [19.08, 72.88],
  'sao paulo': [-23.55, -46.63],
  'mexico city': [19.43, -99.13],
  'cape town': [-33.93, 18.42],
  lagos: [6.52, 3.38],
  cairo: [30.04, 31.24],
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Haversine distance between two points in kilometres */
export function distanceKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** Resolve a city + country string to approximate coordinates */
export function getCityCoords(
  city: string,
  country: string,
): [number, number] | null {
  const key = city.toLowerCase()
  if (CITY_COORDS[key]) return CITY_COORDS[key]!
  const countryKey = country.toLowerCase()
  if (CITY_COORDS[countryKey]) return CITY_COORDS[countryKey]!
  return null
}

/** Build a Leaflet DivIcon with an inline SVG shape */
export function makeDivIcon(colour: string, shape: string): L.DivIcon {
  const svgs: Record<string, string> = {
    circle: `<svg width="14" height="14"><circle cx="7" cy="7" r="6" fill="${colour}" stroke="white" stroke-width="1.5"/></svg>`,
    star: `<svg width="16" height="16" viewBox="0 0 24 24"><polygon points="12,2 15,9 22,9 16.5,14 18.5,21 12,17 5.5,21 7.5,14 2,9 9,9" fill="${colour}" stroke="white" stroke-width="1"/></svg>`,
    person: `<svg width="14" height="16" viewBox="0 0 14 16"><circle cx="7" cy="4" r="3.5" fill="${colour}" stroke="white" stroke-width="1"/><path d="M1,15 Q1,9 7,9 Q13,9 13,15" fill="${colour}" stroke="white" stroke-width="1"/></svg>`,
    building: `<svg width="14" height="16" viewBox="0 0 14 16"><rect x="2" y="3" width="10" height="12" rx="1" fill="${colour}" stroke="white" stroke-width="1"/><rect x="4" y="5" width="2" height="2" fill="white" rx="0.3"/><rect x="8" y="5" width="2" height="2" fill="white" rx="0.3"/><rect x="4" y="9" width="2" height="2" fill="white" rx="0.3"/><rect x="8" y="9" width="2" height="2" fill="white" rx="0.3"/><rect x="5.5" y="12" width="3" height="3" fill="white" rx="0.3"/></svg>`,
    pin: `<svg width="14" height="18" viewBox="0 0 14 18"><path d="M7,0 C3.1,0 0,3.1 0,7 C0,12 7,18 7,18 S14,12 14,7 C14,3.1 10.9,0 7,0z" fill="${colour}" stroke="white" stroke-width="1"/><circle cx="7" cy="7" r="3" fill="white"/></svg>`,
  }

  return L.divIcon({
    html: svgs[shape] ?? svgs.circle!,
    className: '',
    iconSize: shape === 'star' ? [16, 16] : [14, 16],
    iconAnchor: shape === 'star' ? [8, 8] : [7, 16],
    popupAnchor: [0, -16],
  })
}

/** Blue pin with a numeric badge for aggregated fan demand */
export function makeCountIcon(colour: string, count: number): L.DivIcon {
  const label = count > 99 ? '99+' : String(count)
  return L.divIcon({
    html: `<div style="position:relative;width:28px;height:28px;">
      <svg width="28" height="28" viewBox="0 0 28 28">
        <circle cx="14" cy="14" r="12" fill="${colour}" stroke="white" stroke-width="2"/>
      </svg>
      <span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:white;line-height:1;">${label}</span>
    </div>`,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  })
}
